import type {IntBoolean} from '@myparcel/sdk';
import type {
  CommonResponsesUserError,
  GetShipmentsByIdData,
  GetShipmentsData,
  PostShipments,
  PutShipments,
  ShipmentResponsesPostShipmentsV12,
  ShipmentResponsesShipments,
} from '@myparcel-frontend/core-api-service';
import type {GetShipmentsParameters, ShipmentPutData} from '@myparcel-frontend/sdk';
import type {Shipment, ValueOf} from '@myparcel-frontend/types';
import {useCoreApiService} from '@myparcel-frontend/core-api-service';
import {useSdk, Status} from '@myparcel-frontend/sdk';
import {attemptAsync} from 'es-toolkit';
import {storeToRefs} from 'pinia';
import {ref} from 'vue';
import useShop from '@/composables/useShop/useShop';
import {useShipmentStore} from '@/store/shipmentStore/shipmentStore';
import logToMonitor from '@/utils/logging';

type ShipmentsData = ShipmentResponsesShipments['data'];
type CoreShipment = ShipmentsData['shipments'][number];
type CreatedShipments = ShipmentResponsesPostShipmentsV12['data']['shipments'];

const status = ref<ValueOf<typeof Status>>(Status.IDLE);

export default function useShipments() {
  const {sdk} = useSdk();
  const {currentShop} = useShop();
  const shipmentStore = useShipmentStore();
  const {shipments, results} = storeToRefs(shipmentStore);
  const {setShipments, setShipmentHidden, setDeliveryNotificationEmail} = shipmentStore;
  const {
    getShipments: getCoreApiShipments,
    getShipmentsById: getCoreApiShipmentsById,
    postShipments: postCoreApiShipments,
    putShipments: putCoreApiShipments,
  } = useCoreApiService();

  const fetchShipments = async (params: GetShipmentsData['query']): Promise<ShipmentsData> => {
    const [error, response] = await attemptAsync(() => getCoreApiShipments(params));

    if (error || !response) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Error fetching shipments',
        _myparcel_filters: JSON.stringify(params),
      });
      throw error as Error;
    }

    return response;
  };

  /**
   * Fetch the shipment list and keep it in the store so single-shipment mutations
   * (e.g. hiding) can be reflected in place, without refetching the whole list.
   * We do want to fetch the list on every get, so we have the most up-to-date state.
   */
  const getShipments = async (params: GetShipmentsData['query']): Promise<ShipmentsData> => {
    const response = await fetchShipments(params);

    setShipments(response);

    return response;
  };

  const getShipmentsById = async (
    shipmentIds: string[],
    query?: GetShipmentsByIdData['query'],
  ): Promise<ShipmentsData> => {
    const [error, response] = await attemptAsync(() => getCoreApiShipmentsById(shipmentIds, query));

    if (error || !response) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Error fetching shipments by ID',
        _myparcel_filters: JSON.stringify(query),
      });
      throw error as Error;
    }

    return response;
  };

  const getShipmentByExternalIdentifier = async ({
    externalIdentifier,
    includeSecondaryShipments = 0,
  }: {
    externalIdentifier: string;
    includeSecondaryShipments?: IntBoolean;
  }): Promise<CoreShipment | undefined> => {
    let result: CoreShipment[];
    try {
      status.value = Status.PENDING;

      /**
       * This error does not need to be logged to NewRelic.
       *
       * The error is thrown when the `external_identifier` is an empty string,
       * which allows the return type of `getShipmentByExternalIdentifier` to only be of one type.
       */
      if (!externalIdentifier) {
        throw new Error("'external_identifier' parameter argument is undefined or empty");
      }

      const params: GetShipmentsParameters = {
        page: 1,
        size: 1,
        external_identifier: externalIdentifier,
      };

      if (includeSecondaryShipments) {
        Object.assign(params, {
          include_secondary_shipments: includeSecondaryShipments,
        });
      }

      const {shipments} = await fetchShipments(params);

      result = shipments;

      status.value = Status.RESOLVED;
    } catch (error: unknown) {
      status.value = Status.REJECTED;
      throw error;
    }

    return result[0];
  };

  const getShipmentByBarcode = async (barcode: string): Promise<CoreShipment | undefined> => {
    let result: CoreShipment[];

    try {
      status.value = Status.PENDING;

      if (!barcode) {
        throw new Error("'barcode' parameter argument is undefined or empty");
      }

      const {shipments} = await fetchShipments({
        barcode,
      });

      result = shipments;

      status.value = Status.RESOLVED;
    } catch (error: unknown) {
      status.value = Status.REJECTED;
      throw error;
    }

    return result[0];
  };

  /**
   * Post the shipments in the payload and answer with the ones the core API created.
   *
   * The failure is logged here and rethrown, so the caller only decides what the user sees.
   */
  const createShipments = async (payload: PostShipments): Promise<CreatedShipments> => {
    const [error, response] = await attemptAsync(() => postCoreApiShipments(payload));

    if (error) {
      const {request_id: requestId, status_code: statusCode} = error as Partial<CommonResponsesUserError>;

      logToMonitor(error instanceof Error ? error : new Error('Error creating shipments'), {
        _myparcel_extra_info: 'Error creating shipments',
        _myparcel_request_id: requestId,
        _myparcel_status_code: statusCode,
      });
      // cast only for the throw: attemptAsync widens to unknown, which trips only-throw-error
      throw error as Error;
    }

    return response;
  };

  /**
   * Update the shipments in the payload, each addressed by its own `id`.
   *
   * `PUT` is the only endpoint that takes a changed delivery option: a post without an `id` creates a second
   * shipment, and `PATCH /shipments` takes `hidden`, `status` and `delivered` only.
   *
   * The failure is logged here and rethrown, so the caller only decides what the user sees.
   */
  const updateShipments = async (payload: PutShipments): Promise<void> => {
    const [error] = await attemptAsync(() => putCoreApiShipments(payload));

    if (error) {
      const {request_id: requestId, status_code: statusCode} = error as Partial<CommonResponsesUserError>;

      logToMonitor(error instanceof Error ? error : new Error('Error updating shipments'), {
        _myparcel_extra_info: 'Error updating shipments',
        _myparcel_request_id: requestId,
        _myparcel_status_code: statusCode,
      });
      // cast only for the throw: attemptAsync widens to unknown, which trips only-throw-error
      throw error as Error;
    }
  };

  /**
   * Mark a shipment as delivered by patching its status via the core API.
   * Converts the shipmentId from string to number as required by the core API.
   * @TODO https://internal.jira.dmp.zone/browse/OFF-2833: Remove the need for this function once the core-api-service supports the PATCH /shipments endpoint.
   */
  const markAsDelivered = async (shipmentId: string) => {
    return attemptAsync(() => sdk.patchShipments({body: [{id: Number(shipmentId), delivered: 1}]}));
  };

  /**
   * Retrieve a single shipment by its numeric core-API id.
   * Uses the same `getShipment` SDK call the legacy meteor app uses.
   */
  const getShipment = async (id: number): Promise<Shipment | undefined> => {
    status.value = Status.PENDING;

    const [error, response] = await attemptAsync(() => sdk.getShipment({path: {id}}));

    if (error || !response) {
      status.value = Status.REJECTED;
      throw error as Error;
    }

    status.value = Status.RESOLVED;

    return response.shipments[0];
  };

  /**
   * Update a single shipment via the core-API PUT endpoint.
   *
   * The API does not expose PUT on `shipments/:id`; it expects a PUT to the
   * `shipments` collection with the shipment (including its id) in the body
   * array — the same call the legacy meteor app uses.
   */
  const updateShipment = async (id: number, shipment: ShipmentPutData): Promise<Shipment | undefined> => {
    status.value = Status.PENDING;

    const [error, response] = await attemptAsync(() => sdk.putShipments({body: [{...shipment, id}]}));

    if (error || !response) {
      status.value = Status.REJECTED;
      throw error as Error;
    }

    status.value = Status.RESOLVED;

    // The PUT response does not always include a shipments array (it can return only ids/results),
    // so guard against reading [0] of undefined.
    return response.shipments?.[0];
  };

  const setShipmentsHidden = async (shipmentsToHide: Shipment[], hidden: IntBoolean) => {
    const [error, response] = await attemptAsync(() =>
      sdk.patchShipments({body: shipmentsToHide.map((shipment) => ({id: shipment.id, hidden}))}),
    );

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: `Error setting shipment hidden to ${hidden}`,
        _myparcel_ids: JSON.stringify(shipmentsToHide.map((shipment) => shipment.id)),
      });
      throw error as Error;
    }

    shipmentsToHide.forEach((shipment) => setShipmentHidden(shipment.id, hidden));

    return response;
  };

  const enableDeliveryNotification = async (shipment: Shipment): Promise<void> => {
    const email = currentShop.value?.billing?.address?.email;

    if (!email) {
      const missingEmail = 'No billing email available to enable delivery notification';
      const error = new Error(missingEmail);
      logToMonitor(error, {_myparcel_extra_info: missingEmail});
      throw error;
    }

    const [error] = await attemptAsync(() =>
      sdk.postDeliveryNotifications({body: [{id: shipment.id, emails: [email]}]}),
    );

    if (error) {
      logToMonitor(error as Error, {_myparcel_extra_info: 'Error enabling delivery notification'});
      throw error as Error;
    }

    setDeliveryNotificationEmail(shipment.id, email);
  };

  return {
    shipments,
    results,
    getShipments,
    getShipment,
    updateShipment,
    getShipmentsById,
    getShipmentByBarcode,
    getShipmentByExternalIdentifier,
    createShipments,
    updateShipments,
    markAsDelivered,
    setShipmentsHidden,
    enableDeliveryNotification,
    status,
  };
}
